import React from 'react'
import { Box, Typography, Checkbox, CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UploadIcon from '@mui/icons-material/Upload'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { FileTypeIcon } from '../../comps/FileTypeIcon'
import { FS_SM, ICON_XS, ICON_SM, COLOR_PRIMARY, ICON_XXL } from '../../common/theme'
import { DataTable, type DataTableColumn } from '../../comps/DataTable'
import { TaDropZone } from '../../comps/TaDropZone'
import {
  type KbItem,
  shortDate,
} from '../../services/KnowledgeBaseService'

// File types the KB will accept on drop / browse. Kept in sync with the
// matching constant in KbFolderPanel.
const KB_UPLOAD_ACCEPT = '.pdf,.docx,.doc,.xls,.xlsx,.csv,.txt'

// ─── Sort helper ─────────────────────────────────────────────────────────────

function sortFiles(files: KbItem[], sortCol: string, sortDir: 'asc' | 'desc'): KbItem[] {
  return [...files].sort((a, b) => {
    let av: string | number = ''
    let bv: string | number = ''
    if (sortCol === 'name') { av = (a.name ?? '').toLowerCase(); bv = (b.name ?? '').toLowerCase() }
    else if (sortCol === 'savedate') { av = a.savedate ?? ''; bv = b.savedate ?? '' }
    else if (sortCol === 'uploadedby') { av = (a.uploadedby ?? '').toLowerCase(); bv = (b.uploadedby ?? '').toLowerCase() }
    else if (sortCol === 'isIndexed') { av = a.isIndexed ? 1 : 0; bv = b.isIndexed ? 1 : 0 }
    return (av < bv ? -1 : av > bv ? 1 : 0) * (sortDir === 'asc' ? 1 : -1)
  })
}

// ─── Column builder ─────────────────────────────────────────────────────────
// Per-call so the cell renderers can close over the latest props (deletingDocShas etc.)

interface BuildColsOpts {
  deletingDocShas: Set<string>
  onToggleSelect: (file: KbItem, e: React.MouseEvent) => void
}

function buildCols({ deletingDocShas, onToggleSelect }: BuildColsOpts): DataTableColumn<KbItem>[] {
  return [
    {
      id: 'select', iconColumn: true, width: 36,
      render: (file) => {
        const isDeleting = deletingDocShas.has(file.docsha ?? '')
        const isUploading = !file.docsha
        return (
          <Checkbox
            size="small"
            checked={!!file.isselected}
            disabled={isDeleting || isUploading}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(file, e) }}
            inputProps={{ 'data-testid': 'kb_file_checkbox' } as any}
            sx={{ p: 0.5 }}
          />
        )
      },
    },
    {
      id: 'icon', iconColumn: true, width: 32,
      render: (file) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <FileTypeIcon name={file.name} />
        </Box>
      ),
    },
    {
      id: 'name', label: 'Name', flex: 1, width: 0, minWidth: 80,
      render: (file) => (
        <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
          {file.name}
        </Typography>
      ),
    },
    {
      id: 'savedate', label: 'Saved', width: 110, align: 'center',
      render: (file) => (
        <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.5 }}>
          {shortDate(file.savedate)}
        </Typography>
      ),
    },
    {
      id: 'uploadedby', label: 'Uploaded by', width: 140, align: 'center',
      render: (file) => (
        <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
          {file.uploadedby ?? ''}
        </Typography>
      ),
    },
    {
      id: 'isIndexed', iconColumn: true, width: 56,
      render: (file) => {
        if (deletingDocShas.has(file.docsha ?? '')) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <CloseIcon sx={{ fontSize: ICON_SM, color: 'error.main' }} />
              <CircularProgress size={ICON_SM} color="error" />
            </Box>
          )
        }
        if (!file.docsha) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <UploadIcon sx={{ fontSize: ICON_SM, color: COLOR_PRIMARY }} />
              <CircularProgress size={ICON_SM} />
            </Box>
          )
        }
        return file.isIndexed !== undefined
          ? (file.isIndexed
            ? <CheckCircleOutlineIcon sx={{ fontSize: ICON_SM, color: 'success.main' }} />
            : <RadioButtonUncheckedIcon sx={{ fontSize: ICON_SM, opacity: 0.25 }} />)
          : null
      },
    },
  ]
}

// ─── Rows (with empty drop-zone state) ───────────────────────────────────────

export interface KbFileRowsProps {
  files: KbItem[]
  sortCol: string
  sortDir: 'asc' | 'desc'
  onSortChange: (sortCol: string, sortDir: 'asc' | 'desc') => void
  deletingDocShas: Set<string>
  selectMultipleBool: boolean
  isInlineDragging: boolean
  onSetInlineDragging: (v: boolean) => void
  onDropFiles: (fileList: FileList | null) => void
  onClickEmpty: () => void
  onToggleSelect: (file: KbItem, e: React.MouseEvent) => void
  onOpen: (docsha: string) => void
}

export function KbFileRows({
  files, sortCol, sortDir, onSortChange,
  deletingDocShas, selectMultipleBool,
  onDropFiles,
  onToggleSelect, onOpen,
}: KbFileRowsProps) {
  const cols = React.useMemo(
    () => buildCols({ deletingDocShas, onToggleSelect }),
    [deletingDocShas, onToggleSelect],
  )
  const sortedFiles = React.useMemo(
    () => sortFiles(files, sortCol, sortDir),
    [files, sortCol, sortDir],
  )

  return (
    sortedFiles.length == 0 ? (
      <TaDropZone
        testId="kb_files_empty_dropzone"
        onFiles={(fl) => onDropFiles(fl)}
        accept={KB_UPLOAD_ACCEPT}
        multiple
        icon={<CloudUploadOutlinedIcon sx={{ fontSize: ICON_XXL, opacity: 0.4 }} />}
        label="Drop files or click to browse"
        sx={{
          // Maximise into the right-hand pane - fill the available space
          // instead of sitting as a small band.
          boxSizing: 'border-box',
          height: '100%',
          m: 1, py: 0,
          borderColor: 'divider',
          gap: 1,
          bgcolor: 'transparent',
        }}
      />
    ) : (
      <DataTable<KbItem>
        rows={sortedFiles}
        cols={cols}
        rowKey={(f, i) => f.docsha ?? i}
        rowTestId="kb_file_row"
        rowDataAttrs={(f) => ({
          'data-doc-sha': f.docsha ?? '',
          'data-file-name': f.name,
          'data-deleting': deletingDocShas.has(f.docsha ?? '') ? 'true' : 'false',
          'data-uploading': !f.docsha ? 'true' : 'false',
          'data-selected': f.isselected ? 'true' : 'false',
        })}
        rowSx={(f) => ({
          bgcolor: f.isselected ? 'action.selected' : undefined,
          opacity: selectMultipleBool && !f.isselected ? 0.5 : 1,
          transition: 'opacity 0.2s',
        })}
        onRowClick={(f) => {
          const isDeleting = deletingDocShas.has(f.docsha ?? '')
          if (!isDeleting && f.docsha) onOpen(f.docsha)
        }}
        sort={{ colId: sortCol, dir: sortDir }}
        onSortChange={(c, d) => onSortChange(c, d)}
        testIdPrefix="kb_files_table"
      />
    )
  )
}

// ─── Section (header + rows) ─────────────────────────────────────────────────

interface KbFilesSectionProps extends KbFileRowsProps {
  label: string
}

export function KbFilesSection({ label, ...rest }: KbFilesSectionProps) {
  return (
    <Box data-testid="kb_files_section" data-label={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InsertDriveFileOutlinedIcon sx={{ fontSize: ICON_XS, opacity: 0.5 }} />
          <Typography variant="caption" sx={{ opacity: 0.5 }}>{rest.files.length}</Typography>
        </Box>
      </Box>
      <KbFileRows {...rest} />
    </Box>
  )
}
